import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { WidgetFooter, parseISODate } from './common';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Compact month grid over the approved requests the server returned for the
 * previous, current and next month. Paging stays inside that window.
 */
const TeamLeaveCalendarWidget = ({ data }) => {
  const today = useMemo(() => parseISODate(data.today) || new Date(), [data.today]);
  const windowStart = parseISODate(data.window_start);
  const windowEnd = parseISODate(data.window_end);
  const [offset, setOffset] = useState(0);

  const shown = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + offset, 1),
    [today, offset]
  );
  const canPrev = windowStart ? shown > windowStart : offset > -1;
  const canNext = windowEnd ? new Date(shown.getFullYear(), shown.getMonth() + 1, 1) <= windowEnd : offset < 1;

  const requests = useMemo(() => (data.requests || []).map((r) => ({
    ...r,
    start: parseISODate(r.start_date),
    end: parseISODate(r.end_date),
  })), [data.requests]);

  const cells = useMemo(() => {
    const first = new Date(shown.getFullYear(), shown.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7; // Monday-first
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - lead);
    const out = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      const inMonth = d.getMonth() === shown.getMonth();
      const bars = requests.filter((r) => r.start && r.end && d >= r.start && d <= r.end);
      out.push({ date: d, inMonth, isToday: sameDay(d, today), isWeekend: d.getDay() === 0 || d.getDay() === 6, bars });
    }
    // Drop a trailing all-outside week to keep the grid short.
    return out.slice(35).every((c) => !c.inMonth) ? out.slice(0, 35) : out;
  }, [shown, requests, today]);

  const legend = useMemo(() => {
    const byPerson = new Map();
    requests.forEach((r) => {
      const monthStart = new Date(shown.getFullYear(), shown.getMonth(), 1);
      const monthEnd = new Date(shown.getFullYear(), shown.getMonth() + 1, 0);
      if (r.end < monthStart || r.start > monthEnd) return;
      if (!byPerson.has(r.employee_id)) byPerson.set(r.employee_id, r);
    });
    return Array.from(byPerson.values());
  }, [requests, shown]);

  return (
    <>
      <div className="mydesk-month">
        <div className="mydesk-month-head">
          <span className="mydesk-month-title">
            {MONTHS[shown.getMonth()]} {shown.getFullYear()}
            {data.team_size ? <span className="mydesk-month-meta"> {'·'} {data.team_size} people</span> : null}
          </span>
          <div className="mydesk-month-nav">
            <button type="button" onClick={() => setOffset((o) => o - 1)} disabled={!canPrev} aria-label="Previous month">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button type="button" onClick={() => setOffset((o) => o + 1)} disabled={!canNext} aria-label="Next month">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
        <div className="mydesk-dow">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
        <div className="mydesk-cells">
          {cells.map((c) => (
            <div
              key={c.date.toISOString()}
              className={`mydesk-cell ${c.inMonth ? '' : 'is-out'} ${c.isWeekend ? 'is-weekend' : ''} ${c.isToday ? 'is-today' : ''}`}
            >
              <span className="mydesk-cell-num">{c.date.getDate()}</span>
              {c.bars.slice(0, 3).map((r) => (
                <span
                  key={r.id}
                  className="mydesk-bar"
                  style={r.color ? { backgroundColor: r.color } : undefined}
                  title={`${r.employee_name} · ${r.leave_type || 'Absent'}`}
                />
              ))}
              {c.bars.length > 3 && <span className="mydesk-cell-more">+{c.bars.length - 3}</span>}
            </div>
          ))}
        </div>
        {legend.length > 0 && (
          <div className="mydesk-month-legend">
            {legend.map((r) => (
              <span key={r.employee_id}>
                <span className="mydesk-bar mydesk-bar-swatch" style={r.color ? { backgroundColor: r.color } : undefined} />
                {r.employee_name}
              </span>
            ))}
          </div>
        )}
      </div>
      <WidgetFooter label="Open leave management" url={data.url} />
    </>
  );
};

export default TeamLeaveCalendarWidget;
